import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  CircularProgress,
  Divider,
  Stack,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import DataArrayIcon from "@mui/icons-material/DataArray";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useSnackbar } from "notistack";
import { useCreateWhatsappTemplate, useUpdateWhatsappTemplate, useUploadTemplateMedia, useWhatsappNumbers } from "../../hooks/useWhatsapp";
import { parseComponents } from "../../utils/whatsappTemplate";
import { validateTemplate, groupButtonsForSubmit } from "../../utils/whatsappTemplateRules";
import WhatsAppTemplatePreview from "./WhatsAppTemplatePreview";

const HEADER_TYPES = [
  { value: "NONE", label: "None" },
  { value: "TEXT", label: "Text" },
  { value: "IMAGE", label: "Image" },
  { value: "VIDEO", label: "Video" },
  { value: "DOCUMENT", label: "Document" },
];

const MEDIA_ACCEPT = { IMAGE: "image/*", VIDEO: "video/*", DOCUMENT: ".pdf,.doc,.docx" };

const BUTTON_TYPES = [
  { value: "QUICK_REPLY", label: "Quick reply" },
  { value: "URL", label: "Visit website" },
  { value: "PHONE_NUMBER", label: "Call phone number" },
];

// UI cap on total buttons added — the per-type caps Meta actually enforces (2 URL, 1
// phone number, quick replies otherwise) are checked by validateTemplate() instead, since
// they're not a single flat number.
const MAX_BUTTONS = 5;

// A section card — same shape used for Header/Body/Footer/Buttons so the form reads as
// distinct steps instead of one long stack of fields.
function Section({ title, optional, action, children }) {
  return (
    <Box sx={{ borderRadius: "14px", border: "1px solid rgba(0,0,0,0.07)", p: 2, mb: 2, bgcolor: "rgba(255,255,255,0.6)" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.25 }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "#1C1C2E", letterSpacing: 0.4, fontFamily: "Quicksand" }}>
          {title}
          {optional && <Box component="span" sx={{ color: "#9CA3AF", fontWeight: 600, ml: 0.75 }}>optional</Box>}
        </Typography>
        {action}
      </Box>
      {children}
    </Box>
  );
}

function countVariables(text) {
  const matches = (text || "").match(/\{\{\s*\d+\s*\}\}/g);
  return matches ? new Set(matches).size : 0;
}

// Wraps the current text-selection (or appends at the cursor) with the given markdown
// marker — same *bold*/_italic_/~strikethrough~ syntax WhatsApp itself uses.
function wrapSelection(textareaRef, text, setText, marker) {
  const el = textareaRef.current;
  if (!el) return;
  const { selectionStart, selectionEnd } = el;
  const selected = text.slice(selectionStart, selectionEnd) || "text";
  const next = text.slice(0, selectionStart) + marker + selected + marker + text.slice(selectionEnd);
  setText(next);
}


function TemplateBuilderDialog({ open, onClose, template = null }) {
  const isEditing = Boolean(template);
  const { enqueueSnackbar } = useSnackbar();
  const createMutation = useCreateWhatsappTemplate();
  const updateMutation = useUpdateWhatsappTemplate();
  const uploadMutation = useUploadTemplateMedia();
  const { data: numbersData } = useWhatsappNumbers();
  const bodyRef = useRef(null);
  const fileInputRef = useRef(null);

  const numbers = (numbersData?.tokens || []).filter((t) => t.isActive);

  const [name, setName] = useState("");
  const [category, setCategory] = useState("UTILITY");
  const [language, setLanguage] = useState("en_US");
  // Which WABA/number this template is submitted under — only meaningful when creating;
  // an existing template's WABA can't change on edit (see canEditWaba note below).
  const [wabaId, setWabaId] = useState("");
  const [headerType, setHeaderType] = useState("NONE");
  const [headerText, setHeaderText] = useState("");
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreviewUrl, setMediaPreviewUrl] = useState(null);
  // Our own cached copy of the template's CURRENT header media, shown read-only until a
  // new file is picked — Meta never returns a viewable URL for an existing template's
  // media, only an opaque handle, so this is the only way to actually see it again.
  const [existingMediaUrl, setExistingMediaUrl] = useState(null);
  const [bodyText, setBodyText] = useState("");
  const [bodySamples, setBodySamples] = useState([]);
  const [footerText, setFooterText] = useState("");
  const [buttons, setButtons] = useState([]);
  // 1-based index of which {{n}} the deliverables picker should attach to when composing a
  // send (see WhatsAppBulkSendModal.jsx) — null means none designated, the common case.
  // Lives on the template itself (models/whatsappTemplate.js), not sent to Meta.
  const [deliverablesVariableIndex, setDeliverablesVariableIndex] = useState(null);

  const bodyVariableCount = useMemo(() => countVariables(bodyText), [bodyText]);

  // If editing the body drops the variable count below the currently-designated index (the
  // variable it pointed at no longer exists), clear it — a stale designation pointing past
  // the end of the list is worse than none.
  useEffect(() => {
    if (deliverablesVariableIndex != null && deliverablesVariableIndex > bodyVariableCount) {
      setDeliverablesVariableIndex(null);
    }
  }, [bodyVariableCount, deliverablesVariableIndex]);

  // Revoke the object URL when it's replaced or the dialog unmounts — otherwise each
  // selected file leaks a blob URL for the life of the tab.
  useEffect(() => {
    return () => {
      if (mediaPreviewUrl) URL.revokeObjectURL(mediaPreviewUrl);
    };
  }, [mediaPreviewUrl]);

  // Prefill from the template being edited every time the dialog opens on one — covers
  // both "open it fresh" and "close one template's edit, open another's" without a stale
  // carry-over from whichever template was open last.
  useEffect(() => {
    if (!open) return;
    if (template) {
      const parsed = parseComponents(template.components);
      setName(template.name);
      setCategory(template.category);
      setLanguage(template.language);
      setWabaId(template.wabaId || "");
      setHeaderType(parsed.headerType);
      setHeaderText(parsed.headerText);
      setBodyText(parsed.bodyText);
      setBodySamples(parsed.bodySamples);
      setFooterText(parsed.footerText);
      setButtons(parsed.buttons);
      setExistingMediaUrl(template.headerMediaUrl || null);
      setDeliverablesVariableIndex(template.deliverablesVariableIndex ?? null);
    } else if (numbers.length > 0 && !wabaId) {
      // Fresh create — default to the first registered number rather than leaving it
      // unset, so a single-number setup needs zero extra clicks.
      setWabaId(numbers[0].wabaId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, template, numbers.length]);

  const reset = () => {
    setName("");
    setCategory("UTILITY");
    setLanguage("en_US");
    setWabaId("");
    setHeaderType("NONE");
    setHeaderText("");
    if (mediaPreviewUrl) URL.revokeObjectURL(mediaPreviewUrl);
    setMediaFile(null);
    setMediaPreviewUrl(null);
    setExistingMediaUrl(null);
    setBodyText("");
    setBodySamples([]);
    setFooterText("");
    setButtons([]);
    setDeliverablesVariableIndex(null);
  };

  const handleClose = () => {
    if (createMutation.isPending || updateMutation.isPending || uploadMutation.isPending) return;
    reset();
    onClose();
  };

  const handleHeaderTypeChange = (value) => {
    setHeaderType(value);
    if (mediaPreviewUrl) URL.revokeObjectURL(mediaPreviewUrl);
    setMediaFile(null);
    setMediaPreviewUrl(null);
    setExistingMediaUrl(null);
  };

  const handleFilePicked = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (mediaPreviewUrl) URL.revokeObjectURL(mediaPreviewUrl);
    setMediaFile(file);
    setMediaPreviewUrl(headerType === "DOCUMENT" ? null : URL.createObjectURL(file));
  };

  const removeMedia = () => {
    if (mediaPreviewUrl) URL.revokeObjectURL(mediaPreviewUrl);
    setMediaFile(null);
    setMediaPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const insertVariable = () => {
    const nextIndex = bodyVariableCount + 1;
    setBodyText((prev) => `${prev}{{${nextIndex}}}`);
    setBodySamples((prev) => [...prev, ""]);
  };

  const addButton = () => {
    if (buttons.length >= MAX_BUTTONS) return;
    setButtons((prev) => [...prev, { type: "QUICK_REPLY", text: "", url: "", phoneNumber: "" }]);
  };

  const updateButton = (index, patch) => {
    setButtons((prev) => prev.map((b, i) => (i === index ? { ...b, ...patch } : b)));
  };

  const removeButton = (index) => {
    setButtons((prev) => prev.filter((_, i) => i !== index));
  };

  const buildComponents = (headerHandle) => {
    const components = [];

    if (headerType !== "NONE") {
      components.push({
        type: "HEADER",
        format: headerType,
        ...(headerType === "TEXT" ? { text: headerText.trim() } : {}),
        ...(headerHandle ? { example: { header_handle: [headerHandle] } } : {}),
      });
    }

    components.push({
      type: "BODY",
      text: bodyText.trim(),
      ...(bodyVariableCount > 0 ? { example: { body_text: [bodySamples.map((s) => s || "example")] } } : {}),
    });

    if (footerText.trim()) {
      components.push({ type: "FOOTER", text: footerText.trim() });
    }

    if (buttons.length > 0) {
      // Quick-reply and non-quick-reply buttons can't be interleaved — Meta requires two
      // contiguous groups, regardless of the order they were added in the form.
      components.push({
        type: "BUTTONS",
        buttons: groupButtonsForSubmit(buttons).map((b) => ({
          type: b.type,
          text: b.text.trim(),
          ...(b.type === "URL" ? { url: b.url.trim() } : {}),
          ...(b.type === "PHONE_NUMBER" ? { phone_number: b.phoneNumber.trim() } : {}),
        })),
      });
    }

    return components;
  };

  const compliance = useMemo(
    () => validateTemplate({ category, headerType, headerText, footerText, buttons, bodyText }),
    [category, headerType, headerText, footerText, buttons, bodyText]
  );

  const needsMedia = ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerType);
  const canSubmit =
    name.trim() &&
    bodyText.trim() &&
    (headerType !== "TEXT" || headerText.trim()) &&
    (!needsMedia || mediaFile) &&
    compliance.errors.length === 0 &&
    (isEditing || Boolean(wabaId)) &&
    buttons.every((b) => b.text.trim() && (b.type !== "URL" || b.url.trim()) && (b.type !== "PHONE_NUMBER" || b.phoneNumber.trim()));

  const isBusy = createMutation.isPending || updateMutation.isPending || uploadMutation.isPending;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      let headerHandle = null;
      let headerMediaUrl = null;
      if (needsMedia && mediaFile) {
        const res = await uploadMutation.mutateAsync({ file: mediaFile, wabaId });
        headerHandle = res.handle;
        headerMediaUrl = res.url;
      }

      if (isEditing) {
        await updateMutation.mutateAsync({
          id: template._id,
          category,
          components: buildComponents(headerHandle),
          headerMediaUrl,
          deliverablesVariableIndex,
        });
        enqueueSnackbar("Template updated — back in review with Meta", { variant: "success" });
      } else {
        await createMutation.mutateAsync({
          name: name.trim().toLowerCase().replace(/\s+/g, "_"),
          category,
          language,
          wabaId,
          components: buildComponents(headerHandle),
          headerMediaUrl,
          deliverablesVariableIndex,
        });
        enqueueSnackbar("Template submitted to Meta for review", { variant: "success" });
      }
      reset();
      onClose();
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || "Failed to submit template", { variant: "error" });
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "20px" } }}>
      <DialogTitle sx={{ fontWeight: 800, fontFamily: "Quicksand", display: "flex", alignItems: "center", gap: 1 }}>
        <WhatsAppIcon sx={{ color: "#25D366" }} /> {isEditing ? `Edit "${template.name}"` : "New WhatsApp Template"}
      </DialogTitle>
      <DialogContent sx={{ bgcolor: "#FAFBFC" }}>
        {isEditing && (
          <Typography sx={{ fontSize: 12, color: "#B36B00", bgcolor: "#FFF4E5", borderRadius: "8px", px: 1.5, py: 1, mb: 2, mt: 0.5 }}>
            Editing sends this back to Meta for re-review, even if it was already approved. Name and language can't be changed here — create a new template for those.
          </Typography>
        )}
        <Box sx={{ display: "flex", gap: 3, flexDirection: { xs: "column", md: "row" } }}>
          {/* Form */}
          <Box sx={{ flex: 1.3, minWidth: 0 }}>
            <Section title="TEMPLATE">
              <TextField
                fullWidth
                size="small"
                label="Template name"
                placeholder="e.g. payment_processed"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isEditing}
                helperText={isEditing ? "Locked — Meta doesn't allow renaming a template" : "Lowercase, underscores only — Meta will reject anything else"}
                sx={{ mb: 2 }}
              />

              {numbers.length === 0 ? (
                <Typography sx={{ fontSize: 12, color: "#E34C14", mb: 2 }}>
                  No WhatsApp number registered yet — save one via the admin token route first.
                </Typography>
              ) : (
                <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                  <InputLabel id="wa-number-label">WhatsApp Number</InputLabel>
                  <Select
                    labelId="wa-number-label"
                    label="WhatsApp Number"
                    value={wabaId}
                    onChange={(e) => setWabaId(e.target.value)}
                    disabled={isEditing}
                  >
                    {numbers.map((n) => (
                      <MenuItem key={n.wabaId} value={n.wabaId}>
                        {n.displayPhoneNumber}
                      </MenuItem>
                    ))}
                  </Select>
                  {isEditing && (
                    <Typography sx={{ fontSize: 11, color: "#9CA3AF", mt: 0.5 }}>
                      Locked — a template can't move to a different number once created.
                    </Typography>
                  )}
                </FormControl>
              )}

              <Box sx={{ display: "flex", gap: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="wa-category-label">Category</InputLabel>
                  <Select labelId="wa-category-label" label="Category" value={category} onChange={(e) => setCategory(e.target.value)}>
                    <MenuItem value="UTILITY">Utility (transactional)</MenuItem>
                    <MenuItem value="MARKETING">Marketing</MenuItem>
                    <MenuItem value="AUTHENTICATION">Authentication</MenuItem>
                  </Select>
                </FormControl>
                <TextField fullWidth size="small" label="Language code" value={language} onChange={(e) => setLanguage(e.target.value)} disabled={isEditing} />
              </Box>
            </Section>

            <Section title="HEADER" optional>
              <FormControl fullWidth size="small" sx={{ mb: headerType !== "NONE" ? 1.5 : 0 }}>
                <Select value={headerType} onChange={(e) => handleHeaderTypeChange(e.target.value)}>
                  {HEADER_TYPES.map((h) => (
                    <MenuItem key={h.value} value={h.value}>
                      {h.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {headerType === "TEXT" && (
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Header text (max 60 characters)"
                  value={headerText}
                  onChange={(e) => setHeaderText(e.target.value.slice(0, 60))}
                  helperText={`${headerText.length}/60`}
                />
              )}

              {needsMedia && (
                <Box>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={MEDIA_ACCEPT[headerType]}
                    hidden
                    onChange={handleFilePicked}
                  />
                  {!mediaFile && existingMediaUrl ? (
                    <Box
                      onClick={() => fileInputRef.current?.click()}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        border: "1px solid #E0E4E8",
                        borderRadius: "10px",
                        p: 1,
                        cursor: "pointer",
                        "&:hover": { borderColor: "#FF5200" },
                      }}
                    >
                      {headerType === "IMAGE" ? (
                        <Box component="img" src={existingMediaUrl} alt="" sx={{ width: 44, height: 44, borderRadius: "6px", objectFit: "cover", flexShrink: 0 }} />
                      ) : (
                        <Box sx={{ width: 44, height: 44, borderRadius: "6px", bgcolor: "#E9EDEF", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <CloudUploadOutlinedIcon sx={{ fontSize: 20, color: "#8696A0" }} />
                        </Box>
                      )}
                      <Typography sx={{ fontSize: 12.5, color: "#6B7280", fontFamily: "Quicksand" }}>
                        Current {headerType.toLowerCase()} — click to replace (needed to save any edit)
                      </Typography>
                    </Box>
                  ) : !mediaFile ? (
                    <Box
                      onClick={() => fileInputRef.current?.click()}
                      sx={{
                        border: "1.5px dashed #C7CDD1",
                        borderRadius: "10px",
                        py: 3,
                        textAlign: "center",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                        "&:hover": { borderColor: "#FF5200", bgcolor: "rgba(255,82,0,0.04)" },
                      }}
                    >
                      <CloudUploadOutlinedIcon sx={{ fontSize: 26, color: "#8696A0", mb: 0.5 }} />
                      <Typography sx={{ fontSize: 12.5, color: "#6B7280", fontFamily: "Quicksand" }}>
                        {isEditing
                          ? `Re-upload a sample ${headerType.toLowerCase()} to save this edit — the original file isn't kept locally`
                          : `Click to upload a sample ${headerType.toLowerCase()} — Meta needs this to review the template`}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: "#F0F2F5", borderRadius: "10px", p: 1 }}>
                      <Typography sx={{ fontSize: 12.5, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontFamily: "Quicksand" }}>
                        {mediaFile.name}
                      </Typography>
                      <IconButton size="small" onClick={removeMedia}>
                        <CloseRoundedIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              )}
            </Section>

            <Section
              title="BODY"
              action={
                <Stack direction="row" spacing={0.25}>
                  <Tooltip title="Bold">
                    <IconButton size="small" onClick={() => wrapSelection(bodyRef, bodyText, setBodyText, "*")}>
                      <FormatBoldIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Italic">
                    <IconButton size="small" onClick={() => wrapSelection(bodyRef, bodyText, setBodyText, "_")}>
                      <FormatItalicIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Strikethrough">
                    <IconButton size="small" onClick={() => wrapSelection(bodyRef, bodyText, setBodyText, "~")}>
                      <StrikethroughSIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Insert variable">
                    <IconButton size="small" onClick={insertVariable}>
                      <DataArrayIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              }
            >
              <TextField
                inputRef={bodyRef}
                fullWidth
                multiline
                minRows={4}
                size="small"
                placeholder="Hi {{1}}, your payment of {{2}} has been processed."
                value={bodyText}
                onChange={(e) => setBodyText(e.target.value.slice(0, 1024))}
                helperText={`${bodyText.length}/1024 — use *bold*, _italic_, ~strikethrough~`}
                sx={{ mb: bodyVariableCount > 0 ? 1.5 : 0 }}
              />
              {bodyVariableCount > 0 && (
                <Box>
                  <Typography sx={{ fontSize: 11.5, color: "#9CA3AF", mb: 0.75 }}>
                    Sample values (Meta requires these to review the template):
                  </Typography>
                  {Array.from({ length: bodyVariableCount }).map((_, i) => (
                    <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder={`Example for {{${i + 1}}}`}
                        value={bodySamples[i] || ""}
                        onChange={(e) => {
                          const next = [...bodySamples];
                          next[i] = e.target.value;
                          setBodySamples(next);
                        }}
                      />
                      <Tooltip title="Show the deliverables picker on this variable when composing a send">
                        <FormControlLabel
                          sx={{ m: 0, flexShrink: 0 }}
                          control={
                            <Checkbox
                              size="small"
                              checked={deliverablesVariableIndex === i + 1}
                              onChange={() =>
                                setDeliverablesVariableIndex((cur) => (cur === i + 1 ? null : i + 1))
                              }
                            />
                          }
                          label={<Typography sx={{ fontSize: 12 }}>Deliverables</Typography>}
                        />
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
              )}
            </Section>

            <Section title="FOOTER" optional>
              <TextField
                fullWidth
                size="small"
                placeholder="Footer text (max 60 characters)"
                value={footerText}
                onChange={(e) => setFooterText(e.target.value.slice(0, 60))}
              />
            </Section>

            <Section
              title={`BUTTONS (max ${MAX_BUTTONS})`}
              optional
              action={
                buttons.length < MAX_BUTTONS && (
                  <Button size="small" startIcon={<AddCircleOutlineIcon fontSize="small" />} onClick={addButton} sx={{ textTransform: "none" }}>
                    Add
                  </Button>
                )
              }
            >
              {buttons.length === 0 ? (
                <Typography sx={{ fontSize: 12.5, color: "#B0B7BF", fontStyle: "italic" }}>No buttons added</Typography>
              ) : (
                buttons.map((b, i) => (
                  <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 1.5 }}>
                    <FormControl size="small" sx={{ width: 150, flexShrink: 0 }}>
                      <Select value={b.type} onChange={(e) => updateButton(i, { type: e.target.value })}>
                        {BUTTON_TYPES.map((t) => (
                          <MenuItem key={t.value} value={t.value}>
                            {t.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Button text"
                      value={b.text}
                      onChange={(e) => updateButton(i, { text: e.target.value.slice(0, 25) })}
                    />
                    {b.type === "URL" && (
                      <TextField size="small" fullWidth placeholder="https://…" value={b.url} onChange={(e) => updateButton(i, { url: e.target.value })} />
                    )}
                    {b.type === "PHONE_NUMBER" && (
                      <TextField size="small" fullWidth placeholder="+91…" value={b.phoneNumber} onChange={(e) => updateButton(i, { phoneNumber: e.target.value })} />
                    )}
                    <IconButton size="small" onClick={() => removeButton(i)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))
              )}
            </Section>
          </Box>

          {/* Live preview */}
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", position: { md: "sticky" }, top: { md: 16 }, alignSelf: { md: "flex-start" } }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "#1C1C2E", mb: 1.5, alignSelf: "flex-start", fontFamily: "Quicksand", letterSpacing: 0.4 }}>
              PREVIEW — how it looks on WhatsApp
            </Typography>
            <WhatsAppTemplatePreview
              headerType={headerType}
              headerText={headerText}
              headerMediaUrl={mediaPreviewUrl || existingMediaUrl}
              headerFileName={mediaFile?.name}
              bodyText={bodyText}
              bodyVariableSamples={bodySamples}
              footerText={footerText}
              buttons={buttons}
            />
          </Box>
        </Box>

        {(compliance.errors.length > 0 || compliance.warnings.length > 0) && (
          <Box sx={{ mt: 2 }}>
            {compliance.errors.length > 0 && (
              <Box sx={{ borderRadius: "12px", bgcolor: "rgba(227,76,20,0.06)", border: "1px solid rgba(227,76,20,0.25)", p: 1.5, mb: compliance.warnings.length > 0 ? 1 : 0 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#E34C14", display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                  <ErrorOutlineIcon sx={{ fontSize: 15 }} /> Will be rejected by Meta
                </Typography>
                {compliance.errors.map((e, i) => (
                  <Typography key={i} sx={{ fontSize: 12.5, color: "#B23A10", pl: 2.75 }}>
                    • {e}
                  </Typography>
                ))}
              </Box>
            )}
            {compliance.warnings.length > 0 && (
              <Box sx={{ borderRadius: "12px", bgcolor: "#FFF4E5", border: "1px solid #F5DBA6", p: 1.5 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#B36B00", display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                  <WarningAmberIcon sx={{ fontSize: 15 }} /> Worth checking before you submit
                </Typography>
                {compliance.warnings.map((w, i) => (
                  <Typography key={i} sx={{ fontSize: 12.5, color: "#8A5A00", pl: 2.75 }}>
                    • {w}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} sx={{ textTransform: "none", borderRadius: "10px" }} disabled={isBusy}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit || isBusy}
          sx={{ textTransform: "none", borderRadius: "10px", boxShadow: "none", bgcolor: "primary.main", "&:hover": { bgcolor: "primary.dark" } }}
        >
          {isBusy ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : isEditing ? "Save & resubmit" : "Submit for review"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default TemplateBuilderDialog;
